import moment from 'moment-timezone';
import { clock } from '../clock';

// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
export interface DateString extends String {}

/**
 * @deprecated Use `@deltasierra/utilities-assertions` instead
 * @param val - Month number
 */
export type MonthZeroBased = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

/**
 * @deprecated Use `@deltasierra/utilities-assertions` instead
 * @param val - Month number
 */
export function assertIsMonthZeroBased(val: number): asserts val is MonthZeroBased {
    if (val < 0 || val > 11) {
        throw new Error(`Provided value ${val} does not appear to be a valid MonthZeroBased`);
    }
}

export type MonthOneBased = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export function getMonthOneBased(date: Date): MonthOneBased {
    return (date.getMonth() + 1) as MonthOneBased;
}

export function getUTCMonthOneBased(date: Date): MonthOneBased {
    return (date.getUTCMonth() + 1) as MonthOneBased;
}

export interface DateRangeInclusive {
    startDate: Date;
    endDate: Date;
}

const enum Days {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
}

const weekendDays = [Days.Sunday, Days.Saturday];

export interface HasDate {
    date: Date | string;
}

export function isDateString(date: Date | string): date is string {
    return typeof date === 'string';
}

export function coerceDate(date: Date | string): Date {
    if (isDateString(date)) {
        return new Date(date);
    } else {
        return date;
    }
}

export function getDateAsUtc(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

export function utcToLocalDate(date: Date, hour = 0): Date {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), hour);
}

export function getUtcDateOnly(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

export function addDaysToUtc(date: Date, days: number): Date {
    const result = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days));
    return result;
}

export function isUtcMidnight(date: Date): boolean {
    // Don't check milliseconds, that seems excessive
    return date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0;
}

/**
 * Get the date of the last weekday **before** the given date.
 * This function assumes that dates are in UTC to avoid having to specify the
 * timezone.
 *
 * @example
 * const tuesday = new Date('2019-07-09') // Tuesday, 9th July 2019
 * console.log(findPreviousWeekday(tuesday)) // Output: Monday, 8th July 2019
 *
 * const monday = new Date('2019-07-08') // Monday, 8th July 2019
 * console.log(findPreviousWeekday(monday)) // Output: Friday, 5th July 2019
 *
 * const sunday = new Date('2019-07-07') // Sunday, 7th July 2019
 * console.log(findPreviousWeekday(sunday)) // Output: Friday, 5th July 2019
 * @param date - The date to start searching from
 * @returns The UTC date of the previous weekday
 */
export function findPreviousWeekday(date: Date): Date {
    if (!isUtcMidnight(date)) {
        throw new Error('Input date must be in UTC midnight');
    }

    let prevDate = new Date(date.getTime());

    do {
        prevDate = addDaysToUtc(prevDate, -1);
    } while (isWeekend(prevDate));

    return prevDate;
}

/**
 * Assumes startDate is in UTC midnight. This is to avoid specifying the timezone to work in.
 *
 * @param startDate - the starting date in UTC midnight.
 * @param numWeekdays - the number of weekdays to subtract.
 * @returns The date minus the number of weekdays
 */
export function subtractWeekdays(startDate: Date, numWeekdays: number): Date {
    let outDate = startDate;
    while (numWeekdays > 0) {
        outDate = findPreviousWeekday(outDate);
        // eslint-disable-next-line no-param-reassign
        numWeekdays--;
    }
    return outDate;
}

/**
 * Depending on the time zone, the UTC date could be the previous day.
 * i.e. Saturday may be considered a Friday, Monday may be considered a Sunday.
 *
 * @param date - a Date in UTC
 * @returns Boolean of whether the date is a weekend.
 */
export function isWeekend(date: Date): boolean {
    if (!isUtcMidnight(date)) {
        throw new Error('Input date must be in UTC midnight');
    }

    return date && weekendDays.includes(date.getUTCDay());
}

export function isWeekendMoment(date: moment.Moment): boolean {

    return date && weekendDays.includes(date.day());
}

export function midnight(dateTime: Date): Date {
    return new Date(Date.UTC(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate()));
}

export function startOfDay(dateTime: moment.Moment): moment.Moment {
    return dateTime.startOf('day');
}

export function getNextDay(date: Date): Date {
    const nextDay = midnight(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
}

export function isOnOrAfterDay(dateToCompare: moment.Moment, anchorDate: moment.Moment): boolean {
    return dateToCompare.isSame(anchorDate, 'day') || dateToCompare.isAfter(anchorDate, 'day');
}

export function isOnOrAfterNextDay(dateToCompare: Date, anchorDate: Date): boolean {
    const nextDay = getNextDay(anchorDate);
    const diff = midnight(dateToCompare).getTime() - nextDay.getTime();
    return diff >= 0;
}

export function getOneYearMinusOneDayFromNow(): Date {
    return moment(clock.now()).add(1, 'year').subtract(1, 'day').toDate();
}

function isString(input: any): input is string {
    return typeof input === 'string';
}

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
function isArray<T>(input: T[] | any): input is T[] {
    return input instanceof Array;
}

export function isIsoDateString(value: string, strict?: boolean): boolean {
    const looksLikeIsoDateString = moment(value || '', moment.ISO_8601, true).isValid();

    if (!strict) {
        return looksLikeIsoDateString;
    }

    /*
     * "Strict" mode ignores year-only ISO date string. Even though year-only date strings are valid ISO8601, they
     * break the frontend date parsing logic.
     */
    return looksLikeIsoDateString && !!/^\d{4}-\d{2}/.exec(value);
}

export function convertDateStringToDate(value: string): Date {
    return moment(value || '', moment.ISO_8601, true).toDate();
}

export function isDateInstance<T>(input: Date | T): input is Date {
    return input instanceof Date;
}

/*
 * This list is for fields that we know will not be dates from the db
 * This pipe will automatically turn anything string that is ISO compliant into a date
 * This can cause trouble with things like text fields in templates, that should be rendered with the text as given
 */
const nonDateFieldWhiteList = ['ClubNumber', 'fitwareClubNumber', 'text', 'title'];

export function convertDateStringsToDates<T>(input: T, strict?: boolean): Array<Date | T> | Date | T {
    if (isString(input) && isIsoDateString(input, strict)) {
        return convertDateStringToDate(input);
    } else if (typeof input !== 'object' || input === null) {
        return input;
    } else if (isDateInstance(input)) {
        return input;
    } else if (isArray(input)) {
        return input.map(entry => convertDateStringsToDates(entry, strict) as any);
    }

    const output: any = {};
    const propertyNames = Object.getOwnPropertyNames(input);
    propertyNames.forEach(key => {
        if (!nonDateFieldWhiteList.includes(key)) {
            const propertyValue = (input as any)[key];
            output[key] = convertDateStringsToDates(propertyValue, strict);
        } else {
            output[key] = (input as any)[key];
        }
    });
    output.prototype = (input as any).prototype;

    return output;
}

export function convertDateToUnixSeconds(date: Date): number {
    return date.getTime() / 1000;
}

/**
 * Return the maximum date from a given set of dates.
 *
 * @param dates - The dates to search for the maximum
 * @returns The maximum date in the set
 */
export function maxDate(...dates: Date[]): Date {
    return dates.reduce((dateOne, dateTwo) => (dateOne > dateTwo ? dateOne : dateTwo));
}

/**
 * Return the minimum date from a given set of dates.
 *
 * @param dates - The set of dates to search for the miminum
 * @returns The minimum date in the set
 */
export function minDate(...dates: Date[]): Date {
    return dates.reduce((dateOne, dateTwo) => (dateOne < dateTwo ? dateOne : dateTwo));
}

/**
 * Check the provided date to see if it is in the past. If it is return "null". This is useful when you want to provide
 * a default value if the given date is too old.
 *
 * @example
 * const scheduledTime = convertPastDateToNull(userScheduledTime) ?? new Date();
 * @param date - The date to check
 * @returns Either null or the given date
 */
export function convertPastDateToNull(date: Date): Date | null {
    const now = moment();
    return now.isBefore(date) ? date : null;
}
